import React, { createContext, useContext, ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_BASE_URL } from '../utils/apiConfig';

export interface AuthUser {
  id: number;
  username: string;
  name?: string;
  email?: string;
  roles?: string[];
  permissions?: string[];
}

interface AuthContextType {
  user: AuthUser | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  canAccessAdminPanel: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Configure axios to send cookies with all requests (for httpOnly cookies)
axios.defaults.withCredentials = true;

// Note: With httpOnly cookies, we cannot read tokens from JavaScript
// Backend sets tokens in httpOnly cookies, and they are automatically sent with requests
// We only need to check if user is authenticated via API call to /auth/me/

const clearTokens = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
};

// Rate limiting for token refresh (prevent brute force)
let refreshAttempts = 0;
let lastRefreshTime = 0;
const MAX_REFRESH_ATTEMPTS = 5;
const REFRESH_WINDOW_MS = 60000; // 1 minute

const refreshAccessToken = async (): Promise<string | null> => {
  const now = Date.now();
  
  // Rate limiting: reset counter if window expired
  if (now - lastRefreshTime > REFRESH_WINDOW_MS) {
    refreshAttempts = 0;
  }
  
  // Rate limiting: prevent too many refresh attempts
  if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
    console.warn('Too many token refresh attempts. Please login again.');
    clearTokens();
    return null;
  }
  
  refreshAttempts++;
  lastRefreshTime = now;

  try {
    // Use httpOnly cookies - refresh token is sent automatically by browser
    // Backend reads refresh token from cookie, not from request body
    const response = await axios.post(
      `${API_BASE_URL}/auth/refresh/`,
      {}, // Empty body - token comes from httpOnly cookie
      {
        // Skip request interceptor for this call
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true, // Ensure cookies are sent
      }
    );
    
    // With httpOnly cookies, backend sets tokens in response cookies
    // We don't need to manually store them
    // Just check if request was successful
    if (response.status === 200) {
      // Reset rate limiting on success
      refreshAttempts = 0;
      
      // Tokens are now in httpOnly cookies set by backend
      // Return a dummy value to indicate success (we can't read the actual token)
      return 'token_refreshed';
    }
    return null;
  } catch (error: any) {
    // Don't log sensitive error details in production
    if (process.env.NODE_ENV === 'development') {
      console.error('Token refresh failed:', error?.response?.status || error?.message);
    }
    
    // If it's a 401, token is invalid - clear everything
    if (error?.response?.status === 401) {
      clearTokens();
    }
    
    return null;
  }
};

axios.interceptors.request.use(
  async (config) => {
    try {
      // With httpOnly cookies, tokens are automatically sent by browser
      // We don't need to manually add Authorization header
      // Just ensure withCredentials is set for all requests
      config.withCredentials = true;
      
      return config;
    } catch (error) {
      // Don't log sensitive errors in production
      if (process.env.NODE_ENV === 'development') {
        console.error('Request interceptor error:', error);
      }
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    try {
      const originalRequest = error.config;

      // Skip if no config (network error, etc.)
      if (!originalRequest) {
        return Promise.reject(error);
      }

      // Skip if already retried or not a 401 error
      if (error.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      // Skip refresh for refresh endpoint to avoid loops
      if (originalRequest.url?.includes('/auth/refresh/')) {
        return Promise.reject(error);
      }

      // Prevent infinite retry loops - max 1 retry per request
      originalRequest._retry = true;

      const newToken = await refreshAccessToken();
      if (newToken) {
        // With httpOnly cookies, new token is already set by backend in response cookie
        // We don't need to manually add Authorization header
        // Just retry the request - cookies will be sent automatically
        return axios(originalRequest);
      }

      // If refresh failed, clear tokens and reject
      clearTokens();
      return Promise.reject(error);
    } catch (interceptorError) {
      // Don't log sensitive errors in production
      if (process.env.NODE_ENV === 'development') {
        console.error('Response interceptor error:', interceptorError);
      }
      return Promise.reject(error);
    }
  },
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();

  const { data: user, isLoading } = useQuery({
    queryKey: ["currentUser"],
    queryFn: async () => {
      // With httpOnly cookies, we can't check token existence in JavaScript
      // Just try to call API - if token is missing/expired, backend will return 401
      // Response interceptor will handle refresh automatically
      try {
        const { data } = await axios.get(`${API_BASE_URL}/auth/me/`);
        return data;
      } catch (error: any) {
        // If 401, try to refresh token once
        if (error.response?.status === 401) {
          const newToken = await refreshAccessToken();
          if (newToken) {
            // Retry the request after refresh
            const { data } = await axios.get(`${API_BASE_URL}/auth/me/`);
            return data;
          }
        }
        throw error;
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
    enabled: true, // Always enabled - cookies are checked by backend
  });

  const loginMutation = useMutation({
    mutationFn: async ({
      username,
      password,
    }: {
      username: string;
      password: string;
    }) => {
      const { data } = await axios.post(`${API_BASE_URL}/auth/login/`, {
        username,
        password,
      });
      return data;
    },
    onSuccess: (data) => {
      // With httpOnly cookies, backend sets tokens in response cookies
      // We don't need to manually store them
      // Just update user data in cache
      if (data.user) {
        queryClient.setQueryData(["currentUser"], data.user);
      }
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await axios.post(`${API_BASE_URL}/auth/logout/`, {});
    },
    onSuccess: () => {
      clearTokens();
      queryClient.setQueryData(["currentUser"], null);
      queryClient.clear();
    },
  });

  const login = async (username: string, password: string) => {
    await loginMutation.mutateAsync({ username, password });
  };

  const logout = () => {
    logoutMutation.mutate();
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    if (user.roles?.includes("admin")) return true;
    return !!user.permissions?.includes(permission);
  };

  const canAccessAdminPanel =
    hasPermission("users.view") || hasPermission("users.create");

  const value: AuthContextType = {
    user: user || null,
    login,
    logout,
    isLoading: isLoading || loginMutation.isPending || logoutMutation.isPending,
    isAuthenticated: !!user,
    hasPermission,
    canAccessAdminPanel,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
